import { ElementType, lazy, Suspense } from 'react';
import { Navigate, useLocation, useRoutes } from 'react-router-dom';
// hooks
import useAuth from '../hooks/useAuth';
import LogoOnlyLayout from '../layouts/LogoOnlyLayout';
// layouts
import DashboardLayout from '../layouts/dashboard';
import SetupLayout from '../layouts/setup';

import SettingsLayout from 'src/layouts/settings';

// guards
import AuthGuard from '../guards/AuthGuard';
import GuestGuard from '../guards/GuestGuard';
// import RoleBasedGuard from '../guards/RoleBasedGuard';
// config
import { PATH_AFTER_LOGIN } from '../config';
// components
import ClassLayout from 'src/layouts/class';
import ClubLayout from 'src/layouts/club';
import MemberLayout from 'src/layouts/member';
import ProductLayout from 'src/layouts/product';
import LoadingScreen from '../components/LoadingScreen';

// ----------------------------------------------------------------------

const Loadable = (Component: ElementType) => (props: any) => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { pathname } = useLocation();

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { isAuthenticated } = useAuth();

  const isDashboard = pathname.includes('/dashboard') && isAuthenticated;

  return (
    <Suspense fallback={<LoadingScreen isDashboard={isDashboard} />}>
      <Component {...props} />
    </Suspense>
  );
};

export default function Router() {
  return useRoutes([
    {
      path: 'auth',
      children: [
        {
          path: 'signin',
          element: (
            <GuestGuard>
              <Login />
            </GuestGuard>
          ),
        },
        {
          path: 'register',
          element: (
            <GuestGuard>
              <Register />
            </GuestGuard>
          ),
        },
        {
          path: 'redeem',
          element: (
            <GuestGuard>
              <Redeem />
            </GuestGuard>
          ),
        },
        {
          path: 'confirm',
          element: (
            <GuestGuard>
              <Confirm />
            </GuestGuard>
          ),
        },
        {
          path: 'oauth',
          element: (
            <AuthGuard>
              <OAuth />
            </AuthGuard>
          ),
        },
        { path: 'login-unprotected', element: <Login /> },
        { path: 'register-unprotected', element: <Register /> },
        { path: 'reset-password', element: <ResetPassword /> },
        { path: 'new-password', element: <NewPassword /> },
        { path: 'verify', element: <VerifyCode /> },
      ],
    },

    //Sign up Routes
    {
      path: 'setup',
      element: (
        <AuthGuard>
          <SetupLayout />
        </AuthGuard>
      ),
      children: [
        { path: 'company', element: <SetupCompany />, index: true },
        { path: 'club', element: <SetupClub /> },
        { path: 'payment', element: <SetupPayment /> },
      ],
    },

    // Dashboard Routes
    {
      path: 'dashboard',
      element: (
        <AuthGuard>
          <DashboardLayout />
        </AuthGuard>
      ),
      children: [
        { element: <Navigate to={PATH_AFTER_LOGIN} replace />, index: true },
        { path: 'app', element: <GeneralApp /> },
        {
          path: 'members',
          children: [
            { path: '', element: <MemberList />, index: true },
            { path: 'new', element: <MemberCreate /> },
            {
              path: ':id',
              element: <MemberLayout />,
              children: [
                { path: 'home', element: <MemberHome />, index: true },
                { path: 'detail', element: <MemberDetail /> },
                { path: 'billing', element: <MemberBilling /> },
                { path: 'messaging', element: <MemberMessaging /> },
                { path: 'membership', element: <MemberMembership /> },
                { path: 'accessControl', element: <AccessControl /> },
                { path: 'membership/edit', element: <MemberMembershipEdit /> },
                { path: 'membership/suspend', element: <MemberMembershipSuspend /> },
                { path: 'membership/cancel', element: <MemberMembershipCancel /> },
                { path: 'membership/select', element: <MembershipSelect /> },
                { path: 'membership/change', element: <MembershipChange /> },
                { path: 'membership/payment', element: <MembershipPayment /> },
                { path: 'membership/signature', element: <MembershipSignature /> },
                { path: 'membership/package/select', element: <PackageSelect /> },
                {
                  path: 'membership/payInFull',
                  element: <MembershipPayInFull />,
                },
                { path: 'membership/trial/select', element: <TrialSelect /> },
                { path: 'portalAccount', element: <MemberPortal /> },
              ],
            },
          ],
        },
        {
          path: 'class/types',
          children: [
            { path: 'list', element: <ClassTypeList />, index: true },
            { path: 'new', element: <ClassTypeCreate /> },
            { path: ':id', element: <ClassTypeEdit /> },
          ],
        },
        {
          path: 'class',
          children: [
            { path: 'schedule', element: <ClassSchedule />, index: true },
            { path: 'schedule/:clubId', element: <ClassSchedule /> },
            {
              path: ':classId',
              element: <ClassLayout />,
              children: [
                { path: 'attendance', element: <ClassAttendance />, index: true },
                { path: 'details', element: <ClassDetails /> },
              ],
            },
          ],
        },
        {
          path: 'memberships',
          children: [
            { path: 'list', element: <MembershipList />, index: true },
            { path: ':id', element: <MembershipEdit /> },
            { path: 'new', element: <MembershipCreate /> },
          ],
        },
        {
          path: 'packages',
          children: [
            { path: 'list', element: <PackageList />, index: true },
            { path: ':id', element: <PackageEdit /> },
            { path: 'new', element: <PackageCreate /> },
          ],
        },
        {
          path: 'trials',
          children: [
            { path: 'list', element: <TrialList />, index: true },
            { path: ':id', element: <TrialEdit /> },
            { path: 'new', element: <TrialCreate /> },
          ],
        },
        {
          path: 'personalTraining',
          children: [
            { path: 'services', element: <PTServices />, index: true },
            { path: 'services/new', element: <PTServiceCreate /> },
            { path: 'services/:id', element: <PTServiceEdit /> },
            { path: 'schedule/club/:clubId', element: <PTSchedule /> },
            { path: 'schedule/staff/:staffId', element: <PTSchedule /> },
            { path: 'schedule', element: <PTSchedule /> },
          ],
        },
        {
          path: 'templates',
          children: [
            { element: <Templates />, index: true },
            { path: ':id', element: <MessageTemplate /> },
          ],
        },
        {
          path: 'automations',
          children: [
            { path: '', element: <AutomationList />, index: true },
            { path: ':clubId/paymentFollowUp', element: <PaymentFollowUp /> },
          ],
        },
        {
          path: 'reports',
          children: [
            { path: 'members', element: <MembersReport /> },
            { path: 'subscriptions', element: <SubscriptionsReport /> },
            { path: 'suspensions', element: <SuspensionsReport /> },
            { path: 'attendance', element: <AttendanceReport /> },
            { path: 'cancellations', element: <CancellationsReport /> },
            { path: 'currentRevenue', element: <CurrentRevenueReport /> },
            { path: 'upcomingRevenue', element: <UpcomingRevenueReport /> },
            { path: 'posRevenue', element: <PosRevenueReport /> },
            { path: 'overdue', element: <OverduePaymentsReport /> },
            { path: 'visits', element: <VisitReport /> },
          ],
        },
        {
          path: 'productCategories',
          children: [
            { element: <ProductCategories />, index: true },
            { path: 'new', element: <NewProductCategory /> },
            { path: ':id', element: <EditProductCategory /> },
          ],
        },
        {
          path: 'products',
          children: [
            { element: <ProductList />, index: true },
            { path: 'new', element: <NewProduct /> },
            {
              path: ':id',
              element: <ProductLayout />,
              children: [
                { path: 'manage', element: <ManageProduct />, index: true },
                { path: 'stock', element: <ProductStock /> },
                { path: 'stock/new', element: <NewProductStock /> },
                { path: 'stock/:clubId/:productStockId', element: <ManageProductStock /> },
              ],
            },
          ],
        },
        {
          path: 'shop',
          children: [
            { element: <ShopProductListing />, index: true },
            { path: ':clubId/:id', element: <ShopProductDetail /> },
            { path: 'checkout/:id', element: <Checkout /> },
          ],
        },
        {
          path: 'orders',
          children: [
            { element: <OrderList />, index: true },
            { path: ':id', element: <OrderDetail /> },
          ],
        },
        {
          path: 'tasks',
          children: [
            { path: 'myTasks', element: <MyTasks /> },
            { path: 'manage', element: <ManageTasks /> },
            { path: 'requests', element: <Requests /> },
          ],
        },
        {
          path: 'imports',
          children: [
            { path: 'new', element: <NewImport /> },
            { path: ':id/upload', element: <UploadImportFile /> },
            { path: ':id/mapping', element: <ImportMapping /> },
            { path: ':id/mapData', element: <ImportMapData /> },
            { path: ':id/confirm', element: <ImportConfirm /> },
            { path: ':id/results', element: <ImportResults /> },
          ],
        },
        {
          path: 'settings',
          element: <SettingsLayout />,
          children: [
            { path: 'profile', element: <Profile />, index: true },
            { path: 'preferences', element: <Preferences /> },
            { path: 'company', element: <Company /> },
            { path: 'team', element: <Team /> },
            { path: 'team/newInvite', element: <NewInvite /> },
            { path: 'team/:staffId', element: <TeamMember /> },
            { path: 'billing', element: <Billing /> },
            { path: 'notifications', element: <Notifications /> },
            { path: 'changePassword', element: <ChangePassword /> },
            { path: 'emailDomain', element: <EmailDomain /> },
            { path: 'integrations', element: <CompanyIntegrations /> },
            { path: 'customFields', element: <CustomFields /> },
            { path: 'integrations/gymleads', element: <GymLeadsCompanyIntegration /> },
            {
              path: 'clubs',
              children: [
                { path: '', element: <ClubList />, index: true },
                { path: 'new', element: <NewClubPage /> },
                {
                  path: ':id',
                  element: <ClubLayout />,
                  children: [
                    { path: 'detail', element: <ClubDetail />, index: true },
                    { path: 'directDebit', element: <ClubDirectDebit /> },
                    { path: 'webPortal', element: <ClubWebPortal /> },
                    { path: 'sms', element: <ClubSms /> },
                    { path: 'tax', element: <ClubTaxDetails /> },
                    { path: 'gymleads', element: <ClubGymLeads /> },
                    { path: 'imports', element: <ClubImports /> },
                    { path: 'accessControl', element: <ClubAccessControl /> },
                  ],
                },
              ],
            },
          ],
        },
        { path: 'permission-denied', element: <PermissionDenied /> },
      ],
    },

    // Main Routes
    {
      path: '*',
      element: <LogoOnlyLayout />,
      children: [
        { path: '500', element: <Page500 /> },
        { path: '404', element: <Page404 /> },
        { path: '403', element: <Page403 /> },
        { path: '*', element: <Navigate to="/404" replace /> },
      ],
    },
    {
      path: '/',
      element: (
        <GuestGuard>
          <Login />
        </GuestGuard>
      ),
      children: [
        {
          path: 'signin',
          element: (
            <GuestGuard>
              <Login />
            </GuestGuard>
          ),
        },
      ],
    },
    { path: '*', element: <Navigate to="/404" replace /> },
  ]);
}

// AUTHENTICATION
const Login = Loadable(lazy(() => import('../pages/auth/Login')));
const Register = Loadable(lazy(() => import('../pages/auth/Register')));
const ResetPassword = Loadable(lazy(() => import('../pages/auth/ResetPassword')));
const NewPassword = Loadable(lazy(() => import('../pages/auth/NewPassword')));
const VerifyCode = Loadable(lazy(() => import('../pages/auth/VerifyCode')));
const OAuth = Loadable(lazy(() => import('../pages/auth/OAuth')));
const Confirm = Loadable(lazy(() => import('../pages/auth/Confirm')));
const Redeem = Loadable(lazy(() => import('../pages/auth/Redeem')));

// SETUP
const SetupCompany = Loadable(lazy(() => import('../pages/setup/SetupCompany')));
const SetupClub = Loadable(lazy(() => import('../pages/setup/SetupClub')));
const SetupPayment = Loadable(lazy(() => import('../pages/setup/SetupPayment')));

// DASHBOARD
// GENERAL
const GeneralApp = Loadable(lazy(() => import('../pages/dashboard/GeneralApp')));

// Classes
const ClassTypeList = Loadable(lazy(() => import('../pages/dashboard/ClassTypeList')));
const ClassTypeCreate = Loadable(lazy(() => import('../pages/dashboard/ClassTypeCreate')));
const ClassTypeEdit = Loadable(lazy(() => import('../pages/dashboard/ClassTypeEdit')));

const ClassSchedule = Loadable(lazy(() => import('../pages/dashboard/ClassSchedule')));

// Members
const MemberList = Loadable(lazy(() => import('../pages/dashboard/MemberList')));
const MemberCreate = Loadable(lazy(() => import('../pages/dashboard/MemberCreate')));
const MemberHome = Loadable(lazy(() => import('../pages/member/MemberHome')));
const MemberDetail = Loadable(lazy(() => import('../pages/member/MemberDetail')));
const MemberMessaging = Loadable(lazy(() => import('../pages/member/MemberMessaging')));
const MemberBilling = Loadable(lazy(() => import('../pages/member/MemberBilling')));
const MemberMembership = Loadable(lazy(() => import('../pages/member/MemberMembership')));
const MemberMembershipEdit = Loadable(lazy(() => import('../pages/member/EditMemberMembership')));
const MemberMembershipSuspend = Loadable(lazy(() => import('../pages/member/SuspendMembership')));
const MemberMembershipCancel = Loadable(lazy(() => import('../pages/member/CancelMembership')));
const MembershipPayInFull = Loadable(lazy(() => import('../pages/member/MembershipPayInFull')));
const AccessControl = Loadable(lazy(() => import('../pages/member/AccessControl')));
const MemberPortal = Loadable(lazy(() => import('../pages/member/MemberPortal')));

const MembershipSelect = Loadable(
  lazy(() => import('../pages/member/MembershipSelect/MembershipSelect'))
);
const MembershipChange = Loadable(
  lazy(() => import('../pages/member/MembershipSelect/MembershipChange'))
);
const MembershipPayment = Loadable(
  lazy(() => import('../pages/member/MembershipSelect/MembershipPayment'))
);
const MembershipSignature = Loadable(
  lazy(() => import('../pages/member/MembershipSelect/MembershipSignature'))
);

const PackageSelect = Loadable(lazy(() => import('../pages/member/PackageSelect')));
const TrialSelect = Loadable(lazy(() => import('../pages/member/TrialSelect')));

// MEMBERSHIPS
const MembershipList = Loadable(lazy(() => import('../pages/dashboard/MembershipsList')));
const MembershipEdit = Loadable(lazy(() => import('../pages/dashboard/MembershipEdit')));
const MembershipCreate = Loadable(lazy(() => import('../pages/dashboard/MembershipCreate')));

// Packages
const PackageList = Loadable(lazy(() => import('../pages/dashboard/PackageList')));
const PackageEdit = Loadable(lazy(() => import('../pages/dashboard/PackageEdit')));
const PackageCreate = Loadable(lazy(() => import('../pages/dashboard/PackageCreate')));

// Trials
const TrialList = Loadable(lazy(() => import('../pages/dashboard/TrialList')));
const TrialEdit = Loadable(lazy(() => import('../pages/dashboard/TrialEdit')));
const TrialCreate = Loadable(lazy(() => import('../pages/dashboard/TrialCreate')));

// Classes
const ClassAttendance = Loadable(lazy(() => import('../pages/class/Attendance')));
const ClassDetails = Loadable(lazy(() => import('../pages/class/Details')));

// Personal Trainer
const PTServices = Loadable(lazy(() => import('../pages/personalTraining/PTServices')));
const PTServiceCreate = Loadable(lazy(() => import('../pages/personalTraining/PTServiceCreate')));
const PTServiceEdit = Loadable(lazy(() => import('../pages/personalTraining/PTServiceEdit')));
const PTSchedule = Loadable(lazy(() => import('../pages/personalTraining/PTSchedule')));

// Automations
const AutomationList = Loadable(lazy(() => import('../pages/automations/AutomationList')));
const PaymentFollowUp = Loadable(lazy(() => import('../pages/automations/PaymentFollowUp')));

// Templates
const Templates = Loadable(lazy(() => import('../pages/dashboard/Templates')));
const MessageTemplate = Loadable(lazy(() => import('../pages/dashboard/MessageTemplate')));

// Product Categories
const ProductCategories = Loadable(
  lazy(() => import('../pages/productCategory/ProductCategories'))
);
const NewProductCategory = Loadable(
  lazy(() => import('../pages/productCategory/NewProductCategory'))
);
const EditProductCategory = Loadable(
  lazy(() => import('../pages/productCategory/EditProductCategory'))
);

// Tasks
const ManageTasks = Loadable(lazy(() => import('../pages/dashboard/ManageTasks')));
const MyTasks = Loadable(lazy(() => import('../pages/dashboard/TaskList')));
const Requests = Loadable(lazy(() => import('../pages/dashboard/Requests')));

// Shop
const ShopProductListing = Loadable(lazy(() => import('../pages/shop/ProductListing')));
const ShopProductDetail = Loadable(lazy(() => import('../pages/shop/ProductDetail')));
const Checkout = Loadable(lazy(() => import('../pages/shop/Checkout')));

const ProductList = Loadable(lazy(() => import('../pages/product/ProductList')));
const NewProduct = Loadable(lazy(() => import('../pages/product/NewProduct')));
const ManageProduct = Loadable(lazy(() => import('../pages/product/ManageProduct')));
const ProductStock = Loadable(lazy(() => import('../pages/product/ProductStock')));
const NewProductStock = Loadable(lazy(() => import('../pages/product/NewProductStock')));
const ManageProductStock = Loadable(lazy(() => import('../pages/product/EditProductStock')));

// Orders
const OrderList = Loadable(lazy(() => import('../pages/orders/OrderList')));
const OrderDetail = Loadable(lazy(() => import('../pages/orders/OrderDetail')));

//Imports
const NewImport = Loadable(lazy(() => import('../pages/import/NewImport')));
const UploadImportFile = Loadable(lazy(() => import('../pages/import/UploadImportFile')));
const ImportMapping = Loadable(lazy(() => import('../pages/import/ImportMapping')));
const ImportMapData = Loadable(lazy(() => import('../pages/import/ImportMapData')));
const ImportConfirm = Loadable(lazy(() => import('../pages/import/ImportConfirm')));
const ImportResults = Loadable(lazy(() => import('../pages/import/ImportResults')));

// Reports
const MembersReport = Loadable(lazy(() => import('../pages/reports/MembersReport')));
const SubscriptionsReport = Loadable(lazy(() => import('../pages/reports/SubscriptionsReport')));
const SuspensionsReport = Loadable(lazy(() => import('../pages/reports/SuspensionsReport')));
const AttendanceReport = Loadable(lazy(() => import('../pages/reports/AttendanceReport')));
const CancellationsReport = Loadable(lazy(() => import('../pages/reports/CancellationsReport')));
const CurrentRevenueReport = Loadable(lazy(() => import('../pages/reports/CurrentRevenueReport')));
const UpcomingRevenueReport = Loadable(
  lazy(() => import('../pages/reports/UpcomingRevenueReport'))
);
const PosRevenueReport = Loadable(lazy(() => import('../pages/reports/PosRevenueReport')));
const OverduePaymentsReport = Loadable(
  lazy(() => import('../pages/reports/OverduePaymentsReport'))
);
const VisitReport = Loadable(lazy(() => import('../pages/reports/VisitReport')));

// SETTINGS
const Profile = Loadable(lazy(() => import('../pages/settings/Profile')));
const Preferences = Loadable(lazy(() => import('../pages/settings/Preferences')));
const Company = Loadable(lazy(() => import('../pages/settings/Company')));
const ClubList = Loadable(lazy(() => import('../pages/settings/ClubList')));
const NewClubPage = Loadable(lazy(() => import('../pages/settings/NewClub')));
const Team = Loadable(lazy(() => import('../pages/settings/Team')));
const TeamMember = Loadable(lazy(() => import('../pages/settings/TeamMember')));
const NewInvite = Loadable(lazy(() => import('../pages/settings/NewInvite')));
const EmailDomain = Loadable(lazy(() => import('../pages/settings/EmailDomain')));
const CompanyIntegrations = Loadable(lazy(() => import('../pages/settings/Integrations')));
const CustomFields = Loadable(lazy(() => import('../pages/settings/CustomFields')));
const GymLeadsCompanyIntegration = Loadable(
  lazy(() => import('../pages/settings/integrations/GymLeads'))
);

// Club Settings
const ClubDetail = Loadable(lazy(() => import('../pages/club/ClubDetail')));
const ClubDirectDebit = Loadable(lazy(() => import('../pages/club/ClubDirectDebit')));
const ClubWebPortal = Loadable(lazy(() => import('../pages/club/ClubWebPortal')));
const ClubSms = Loadable(lazy(() => import('../pages/club/ClubSms')));
const ClubTaxDetails = Loadable(lazy(() => import('../pages/club/ClubTaxDetails')));
const ClubGymLeads = Loadable(lazy(() => import('../pages/club/ClubGymLeads')));
const ClubImports = Loadable(lazy(() => import('../pages/club/ClubImports')));
const ClubAccessControl = Loadable(lazy(() => import('../pages/club/ClubAccessControl')));

const Billing = Loadable(lazy(() => import('../pages/settings/Billing')));
const Notifications = Loadable(lazy(() => import('../pages/settings/Notifications')));
const ChangePassword = Loadable(lazy(() => import('../pages/settings/ChangePassword')));

// TEST RENDER PAGE BY ROLE
const PermissionDenied = Loadable(lazy(() => import('../pages/dashboard/PermissionDenied')));

const Page500 = Loadable(lazy(() => import('../pages/Page500')));
const Page403 = Loadable(lazy(() => import('../pages/Page403')));
const Page404 = Loadable(lazy(() => import('../pages/Page404')));
